import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { RosterTab } from './RosterTab';
import { CertificationsTab } from './CertificationsTab';
import { CompetencyMatrixTab } from './CompetencyMatrixTab';
import { StoreComparisonTab } from './StoreComparisonTab';

/**
 * `/talent/dashboard` (人材育成ダッシュボード)。旧4画面 (`/talent/skills`・`/talent/certifications`・
 * `/talent/competency-matrix`・`/talent/development-plans`) をタブ統合する
 * (docs/design/v05-talent-development-dashboard-detail.md §3-1/§3-2)。
 *
 * 「会社横断比較」は company_admin (skill:view の all_stores スコープ) のみに表示する
 * (要件§3-2④、TalentCertificationsPage.tsx の canImport 判定と同じ `hasTenantWidePermission` を使う)。
 */

type TabKey = 'roster' | 'certifications' | 'matrix' | 'comparison';
const TAB_LABELS: Record<TabKey, string> = {
  roster: 'チームロースター',
  certifications: '認定の確定・インポート',
  matrix: '人材像マトリクス',
  comparison: '会社横断比較',
};

export function TalentDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const canCompareStores = hasTenantWidePermission(user?.effective_permissions, 'skill', 'view');
  // 他画面からの深いリンク (例: TalentCoveragePage.tsx の「認定の確定・インポートへ」) 用に
  // 初回描画時だけ ?tab=/?node_id= を読む。以後はタブ操作をURLへは反映しない (§6d、過剰なUI同期を避ける)。
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(
    requestedTab && (requestedTab !== 'comparison' || canCompareStores) ? requestedTab : 'roster',
  );
  const initialNodeId = searchParams.get('node_id') ?? '';

  const tabs: TabKey[] = canCompareStores
    ? ['roster', 'certifications', 'matrix', 'comparison']
    : ['roster', 'certifications', 'matrix'];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-page-title text-text-main">人材育成ダッシュボード</h1>
        <p className="text-sm text-text-sub mt-1">配下メンバーの人材像充足状況・育成計画をまとめて確認します。</p>
      </div>

      <div role="tablist" aria-label="人材育成ダッシュボードのタブ" className="flex flex-wrap gap-1 border-b border-surface-line">
        {tabs.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`px-3 py-2.5 text-sm font-semibold min-h-[44px] border-b-2 -mb-px ${
              tab === t ? 'border-misespark-navy text-misespark-navy' : 'border-transparent text-text-sub hover:text-text-body'
            }`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'roster' && <RosterTab initialNodeId={initialNodeId} />}
        {tab === 'certifications' && <CertificationsTab />}
        {tab === 'matrix' && <CompetencyMatrixTab />}
        {tab === 'comparison' && canCompareStores && <StoreComparisonTab />}
      </div>
    </div>
  );
}
